import { useEffect } from "react";

/**
 * useSeo — set per-route SEO tags + JSON-LD without adding a runtime dep.
 *
 * CRA + react-router-dom v6 SPAs share a single index.html, so client-side
 * route changes never update the document <head>. Crawlers that DO execute
 * JS (Googlebot since 2019) read the rendered head, so updating these tags
 * on route change measurably improves SERP titles and snippets.
 *
 * Updates: <title>, <meta name=description>, <link rel=canonical>,
 * <meta property=og:title|description|url|type|image>,
 * <meta name=twitter:title|description|image>, and a
 * <script type="application/ld+json" data-route-jsonld> block for
 * route-specific Schema.org payloads (Article, CollectionPage, etc.).
 *
 * Pass an object once per page render. Falsy fields are skipped.
 */
const SITE_ORIGIN = "https://www.viloraimpex.com";
const ROUTE_JSONLD_ID = "route-jsonld";

function setMeta(attr, value, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setRouteJsonLd(payload) {
  // Always remove the prior route-scoped block first so navigating between
  // routes doesn't accumulate stale schema. The site-wide @graph block in
  // index.html is untouched (it has no id).
  const prior = document.head.querySelector(`script[data-route-id="${ROUTE_JSONLD_ID}"]`);
  if (prior) prior.remove();
  if (!payload) return;
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.setAttribute("data-route-id", ROUTE_JSONLD_ID);
  el.textContent = JSON.stringify(payload);
  document.head.appendChild(el);
}

export function useSeo({
  title,
  description,
  path = "/",
  image,
  imageAlt,
  ogType = "website",
  jsonLd,
}) {
  useEffect(() => {
    if (title) document.title = title;
    const url = `${SITE_ORIGIN}${path}`;
    setMeta("name", "description", description);
    setCanonical(url);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", ogType);
    if (image) setMeta("property", "og:image", image);
    if (imageAlt) setMeta("property", "og:image:alt", imageAlt);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    if (image) setMeta("name", "twitter:image", image);
    if (imageAlt) setMeta("name", "twitter:image:alt", imageAlt);
    setRouteJsonLd(jsonLd);

    return () => {
      // Clean up the route-scoped JSON-LD on unmount so old schema doesn't
      // bleed into the next route during the brief overlap.
      setRouteJsonLd(null);
    };
  }, [title, description, path, image, imageAlt, ogType, jsonLd]);
}
